import React, { useState, useEffect } from 'react';
import { Box, Typography, Select, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import PasswordDisplay from '../PasswordDisplay/PasswordDisplay';
import { generatePassword, calculateEntropy, estimateTimeToCrack } from '../../utils/passwordUtils';

// Icons for each template
import EmailIcon from '@mui/icons-material/Email';
import SocialMediaIcon from '@mui/icons-material/People';
import BankingIcon from '@mui/icons-material/AccountBalance';
import WorkIcon from '@mui/icons-material/Work';
import ShoppingIcon from '@mui/icons-material/ShoppingCart';
import GamingIcon from '@mui/icons-material/SportsEsports';
import EntertainmentIcon from '@mui/icons-material/Movie';
import PasswordManagerIcon from '@mui/icons-material/Security';

const templates = {
  Email: {
    length: 20,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: true,
    description: 'Keeps your email safe. 20 characters with every type for added security.',
    icon: <EmailIcon />,
  },
  'Social Media': {
    length: 12,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: false,
    description: 'Quick and simple for social apps. 12 characters with letters and numbers.',
    icon: <SocialMediaIcon />,
  },
  Banking: {
    length: 16,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: true,
    description: 'Extra safe for banking. 16 characters with all types included.',
    icon: <BankingIcon />,
  },
  Work: {
    length: 20,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: true,
    description: 'Meets workplace rules. 20 characters with all types for protection.',
    icon: <WorkIcon />,
  },
  Shopping: {
    length: 14,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: false,
    description: 'Great for online shops. 14 characters with letters and numbers.',
    icon: <ShoppingIcon />,
  },
  Gaming: {
    length: 18,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: true,
    description: 'Perfect for gaming accounts. 18 characters with all types for safety.',
    icon: <GamingIcon />,
  },
  Entertainment: {
    length: 12,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: false,
    description: 'Entertainment (e.g. Netflix, Spotify). 12 characters with letters and numbers.',
    icon: <EntertainmentIcon />,
  },
  'Password Manager': {
    length: 24,
    useUppercase: true,
    useLowercase: true,
    useDigits: true,
    useSpecial: true,
    description: 'Super secure for managers. 24 characters with all types for safety.',
    icon: <PasswordManagerIcon />,
  },
};

const GuidedMode = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('Email');
  const [passwordData, setPasswordData] = useState(null);

  const generatePasswordForTemplate = (templateKey) => {
    const { length, ...settings } = templates[templateKey];
    const password = generatePassword({ length, useLowercase: true, ...settings }); // Ensure lowercase is always included
    const entropy = calculateEntropy(password);
    const timeToCrack = estimateTimeToCrack(entropy);

    setPasswordData({ password, entropy, timeToCrack });
  };

  useEffect(() => {
    generatePasswordForTemplate(selectedTemplate);
  }, [selectedTemplate]);

  return (
    <Box sx={{ textAlign: 'center', marginTop: '2rem' }}>
      <Typography variant="h5" sx={{ marginBottom: '1rem' }}>
        What's the password for?
      </Typography>
      <Select
        value={selectedTemplate}
        onChange={(e) => setSelectedTemplate(e.target.value)}
        fullWidth
        sx={{
          marginBottom: '1rem',
          backgroundColor: 'background.paper',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem', // Ensure spacing between icon and text
          },
        }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ListItemIcon
              sx={{
                minWidth: '32px', // Ensure consistent icon size
                color: 'text.primary',
              }}
            >
              {templates[selected].icon}
            </ListItemIcon>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '1rem',
                color: 'text.primary',
              }}
            >
              {selected}
            </Typography>
          </Box>
        )}
      >
        {Object.keys(templates).map((key) => (
          <MenuItem value={key} key={key}>
            <ListItemIcon
              sx={{
                minWidth: '32px', // Ensure consistent icon size
                color: 'text.primary',
              }}
            >
              {templates[key].icon}
            </ListItemIcon>
            <ListItemText
              primary={key}
              primaryTypographyProps={{
                sx: { fontWeight: 'normal', fontSize: '1rem' },
              }}
            />
          </MenuItem>
        ))}
      </Select>

      <Typography sx={{ marginBottom: '1rem' }}>
        {templates[selectedTemplate].description}
      </Typography>
      <PasswordDisplay {...passwordData} onRegenerate={() => generatePasswordForTemplate(selectedTemplate)} />
    </Box>
  );
};

export default GuidedMode;
